<template>
  <Transition name="sticky-cta">
    <div
      v-if="visible"
      class="sticky-cta-bar"
      role="region"
      aria-label="Action principale"
    >
      <div class="sticky-cta-bar__inner">
        <router-link
          :to="to"
          class="btn-brand btn-brand--primary sticky-cta-bar__btn"
          @click="onClick"
        >
          {{ label }}
        </router-link>
        <p v-if="hint" class="sticky-cta-bar__hint">{{ hint }}</p>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue';
import { trackCtaClick } from '@/utils/analytics';

const props = defineProps({
  to: { type: String, default: '/diagnostic' },
  label: { type: String, default: 'Faire mon diagnostic' },
  hint: { type: String, default: '' },
  location: { type: String, default: 'sticky_mobile' },
  /** Fraction de scroll avant apparition (0–1) */
  showAfter: { type: Number, default: 0.25 },
});

const visible = ref(false);

onMounted(() => {
  updateVisibility();
  window.addEventListener('scroll', updateVisibility, { passive: true });
  window.addEventListener('resize', updateVisibility, { passive: true });
  document.body.classList.add('has-sticky-cta-ready');
});

onUnmounted(() => {
  window.removeEventListener('scroll', updateVisibility);
  window.removeEventListener('resize', updateVisibility);
  document.body.classList.remove('has-sticky-cta-ready');
  document.body.classList.remove('has-sticky-cta');
});

function updateVisibility() {
  if (typeof window === 'undefined') return;
  if (window.innerWidth > 991) {
    visible.value = false;
    document.body.classList.remove('has-sticky-cta');
    return;
  }
  const doc = document.documentElement;
  const scrollable = Math.max(1, doc.scrollHeight - window.innerHeight);
  visible.value = window.scrollY / scrollable >= props.showAfter;
  document.body.classList.toggle('has-sticky-cta', visible.value);
}

function onClick() {
  trackCtaClick({ location: props.location, label: props.label });
}
</script>

<style scoped>
.sticky-cta-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1040;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  background: rgba(1, 38, 62, 0.96);
  border-top: 1px solid rgba(201, 162, 39, 0.35);
  box-shadow: 0 -8px 24px rgba(1, 38, 62, 0.25);
}

.sticky-cta-bar::before {
  content: '';
  display: block;
  height: 0;
}

.sticky-cta-bar__inner {
  max-width: 480px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
}

.sticky-cta-bar__btn {
  width: 100%;
  text-transform: none;
  letter-spacing: 0.02em;
  font-size: 0.95rem;
}

.sticky-cta-bar__hint {
  margin: 0;
  text-align: center;
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.72);
}

.sticky-cta-enter-active,
.sticky-cta-leave-active {
  transition: transform 0.28s ease, opacity 0.28s ease;
}
.sticky-cta-enter-from,
.sticky-cta-leave-to {
  transform: translateY(110%);
  opacity: 0;
}

@media (min-width: 992px) {
  .sticky-cta-bar {
    display: none;
  }
}
</style>
